// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  features/relatorios/hooks/useRelatorios.ts
//  Hooks React Query para todos os relatórios.
// ════════════════════════════════════════════════════════════════════════════

import { useQuery } from '@tanstack/react-query';
import relatoriosService from '../services/relatoriosService';

const STALE = 5 * 60_000; // 5 min

// ── Vendas ────────────────────────────────────────────────────────────────────

export const useVendasClientesSintetico = (p: Parameters<typeof relatoriosService.vendas.clientesSintetico>[0]) =>
  useQuery({
    queryKey: ['rel-vendas-clientes-sint', p],
    queryFn:  () => relatoriosService.vendas.clientesSintetico(p),
    staleTime: STALE,
    enabled: !!p.dtInicio,
  });

export const useVendasClientesAnalitico = (p: Parameters<typeof relatoriosService.vendas.clientesAnalitico>[0]) =>
  useQuery({
    queryKey: ['rel-vendas-clientes-ana', p],
    queryFn:  () => relatoriosService.vendas.clientesAnalitico(p),
    staleTime: STALE,
    enabled: !!p.dtInicio,
  });

export const useVendasProdutosSintetico = (p: Parameters<typeof relatoriosService.vendas.produtosSintetico>[0]) =>
  useQuery({
    queryKey: ['rel-vendas-produtos-sint', p],
    queryFn:  () => relatoriosService.vendas.produtosSintetico(p),
    staleTime: STALE,
    enabled: !!p.dtInicio,
  });

export const useVendasProdutosAnalitico = (p: Parameters<typeof relatoriosService.vendas.produtosAnalitico>[0]) =>
  useQuery({
    queryKey: ['rel-vendas-produtos-ana', p],
    queryFn:  () => relatoriosService.vendas.produtosAnalitico(p),
    staleTime: STALE,
    enabled: !!p.dtInicio,
  });

export const useVendasFormasPagamento = (p: Parameters<typeof relatoriosService.vendas.formasPagamento>[0]) =>
  useQuery({
    queryKey: ['rel-vendas-formas', p],
    queryFn:  () => relatoriosService.vendas.formasPagamento(p),
    staleTime: STALE,
    enabled: !!p.dtInicio,
  });

export const useEvolucaoVendas = (p: Parameters<typeof relatoriosService.vendas.evolucao>[0]) =>
  useQuery({
    queryKey: ['rel-vendas-evolucao', p],
    queryFn:  () => relatoriosService.vendas.evolucao(p),
    staleTime: STALE,
    enabled: !!p.dtInicio,
  });

export const useHistoricoCliente = (p: Parameters<typeof relatoriosService.vendas.historicoCliente>[0]) =>
  useQuery({
    queryKey: ['rel-historico-cliente', p],
    queryFn:  () => relatoriosService.vendas.historicoCliente(p),
    staleTime: STALE,
    enabled: !!p.clienteId,
  });

// ── Gerencial ─────────────────────────────────────────────────────────────────

export const useAbcClientes = (p: Parameters<typeof relatoriosService.gerencial.abcClientes>[0]) =>
  useQuery({
    queryKey: ['rel-abc-clientes', p],
    queryFn:  () => relatoriosService.gerencial.abcClientes(p),
    staleTime: STALE,
    enabled: !!p.dtInicio,
  });

export const useAbcProdutos = (p: Parameters<typeof relatoriosService.gerencial.abcProdutos>[0]) =>
  useQuery({
    queryKey: ['rel-abc-produtos', p],
    queryFn:  () => relatoriosService.gerencial.abcProdutos(p),
    staleTime: STALE,
    enabled: !!p.dtInicio,
  });

export const useLucratividade = (p: Parameters<typeof relatoriosService.gerencial.lucratividade>[0]) =>
  useQuery({
    queryKey: ['rel-lucratividade', p],
    queryFn:  () => relatoriosService.gerencial.lucratividade(p),
    staleTime: STALE,
    enabled: !!p.dtInicio,
  });

export const useEvolucaoProdutos = (p: Parameters<typeof relatoriosService.gerencial.evolucaoProdutos>[0]) =>
  useQuery({
    queryKey: ['rel-evolucao-produtos', p],
    queryFn:  () => relatoriosService.gerencial.evolucaoProdutos(p),
    staleTime: STALE,
    enabled: !!p.dtInicio,
  });

// ── Fiscais ───────────────────────────────────────────────────────────────────

export const useRelFiscalSaidas = (p: Parameters<typeof relatoriosService.fiscais.saidas>[0]) =>
  useQuery({
    queryKey: ['rel-fiscal-saidas', p],
    queryFn:  () => relatoriosService.fiscais.saidas(p),
    staleTime: STALE,
    enabled: !!p.dtInicio,
  });

export const useRelFiscalEntradas = (p: Parameters<typeof relatoriosService.fiscais.entradas>[0]) =>
  useQuery({
    queryKey: ['rel-fiscal-entradas', p],
    queryFn:  () => relatoriosService.fiscais.entradas(p),
    staleTime: STALE,
    enabled: !!p.dtInicio,
  });

export const useRelVendasCfop = (p: Parameters<typeof relatoriosService.fiscais.cfop>[0]) =>
  useQuery({
    queryKey: ['rel-fiscal-cfop', p],
    queryFn:  () => relatoriosService.fiscais.cfop(p),
    staleTime: STALE,
    enabled: !!p.dtInicio,
  });

// ── Financeiro ────────────────────────────────────────────────────────────────

export const useRelContasReceberAnalitico = (p: Parameters<typeof relatoriosService.financeiro.contasReceberAnalitico>[0]) =>
  useQuery({
    queryKey: ['rel-cr-ana', p],
    queryFn:  () => relatoriosService.financeiro.contasReceberAnalitico(p),
    staleTime: STALE,
    enabled: !!p.dtVencInicio,
  });

export const useRelContasReceberSintetico = (p: Parameters<typeof relatoriosService.financeiro.contasReceberSintetico>[0]) =>
  useQuery({
    queryKey: ['rel-cr-sint', p],
    queryFn:  () => relatoriosService.financeiro.contasReceberSintetico(p),
    staleTime: STALE,
    enabled: !!p.dtVencInicio,
  });

export const useRelContasPagarAnalitico = (p: Parameters<typeof relatoriosService.financeiro.contasPagarAnalitico>[0]) =>
  useQuery({
    queryKey: ['rel-cp-ana', p],
    queryFn:  () => relatoriosService.financeiro.contasPagarAnalitico(p),
    staleTime: STALE,
    enabled: !!p.dtVencInicio,
  });

export const useRelContasPagarSintetico = (p: Parameters<typeof relatoriosService.financeiro.contasPagarSintetico>[0]) =>
  useQuery({
    queryKey: ['rel-cp-sint', p],
    queryFn:  () => relatoriosService.financeiro.contasPagarSintetico(p),
    staleTime: STALE,
    enabled: !!p.dtVencInicio,
  });

export const useRelComprasFornecedor = (p: Parameters<typeof relatoriosService.financeiro.comprasFornecedor>[0]) =>
  useQuery({
    queryKey: ['rel-compras', p],
    queryFn:  () => relatoriosService.financeiro.comprasFornecedor(p),
    staleTime: STALE,
    enabled: !!p.dtInicio,
  });

export const useRelComprasAnalitico = (p: Parameters<typeof relatoriosService.financeiro.comprasAnalitico>[0]) =>
  useQuery({
    queryKey: ['rel-compras-ana', p],
    queryFn:  () => relatoriosService.financeiro.comprasAnalitico(p),
    staleTime: STALE,
    enabled: !!p.dtInicio,
  });
